import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Printer, Search, Settings, Tag } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const LabelPrinting = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [labelConfig, setLabelConfig] = useState(null);
  const [productCodeInput, setProductCodeInput] = useState('');
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cantidad, setCantidad] = useState(1);
  const [incluirPrecio, setIncluirPrecio] = useState(true);
  const [labelData, setLabelData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qzReady, setQzReady] = useState(false);
  const productCodeRef = useRef(null);
  // Selector de impresora
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [showPrinterSelect, setShowPrinterSelect] = useState(false);
  const [pendingEplCommands, setPendingEplCommands] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchLabelConfig();
  }, []);

  // Auto-focus en el input de código de producto
  useEffect(() => {
    if (productCodeRef.current) {
      productCodeRef.current.focus();
    }
  }, []);

  // Inicializar QZ Tray
  useEffect(() => {
    const initQZTray = async () => {
      if (window.qz) {
        try {
          if (!qz.websocket.isActive()) {
            await qz.websocket.connect();
            setQzReady(true);
            console.log('QZ Tray conectado');
          } else {
            setQzReady(true);
          }
        } catch (err) {
          console.log('QZ Tray no disponible:', err.message);
          setQzReady(false);
        }
      }
    };

    initQZTray();

    return () => {
      if (window.qz && qz.websocket.isActive()) {
        qz.websocket.disconnect();
      }
    };
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const fetchLabelConfig = async () => {
    try {
      const response = await axios.get(`${API}/label-config`);
      setLabelConfig(response.data);
    } catch (error) {
      console.error('Error al cargar configuración');
    }
  };

  // Keyboard shortcuts: F2 = búsqueda avanzada, F3 = focus en código
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        setProductSearchOpen(true);
      }
      if (e.key === 'F3') {
        e.preventDefault();
        productCodeRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleProductCodeSubmit = (e) => {
    e.preventDefault();
    if (!productCodeInput.trim()) return;

    const product = products.find(p =>
      p.codigo_interno === productCodeInput.trim() ||
      (Array.isArray(p.codigo_barras) ? p.codigo_barras.includes(productCodeInput.trim()) : p.codigo_barras === productCodeInput.trim())
    );

    if (product) {
      setSelectedProduct(product);
      setProductCodeInput('');
      toast.success(`${product.nombre} seleccionado`);
    } else {
      setProductSearchTerm(productCodeInput.trim());
      setProductSearchOpen(true);
      setProductCodeInput('');
      toast.info('Producto no encontrado. Abriendo búsqueda avanzada...');
    }
  };

  const filteredProductsForSearch = products.filter(p =>
    p.nombre.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    p.codigo_interno?.includes(productSearchTerm) ||
    (Array.isArray(p.codigo_barras) ? p.codigo_barras.some(b => b.includes(productSearchTerm)) : p.codigo_barras?.includes(productSearchTerm)) ||
    p.categoria?.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  const handleGenerateLabels = async () => {
    if (!selectedProduct) {
      toast.error('Selecciona un producto');
      return;
    }

    if (cantidad < 1) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/labels/generate`, {
        producto_id: selectedProduct.id,
        cantidad: cantidad,
        incluir_precio: incluirPrecio
      });

      setLabelData(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar etiqueta');
    } finally {
      setLoading(false);
    }
  };

  // Imprimir directo - genera EPL y envia a impresora via QZ Tray
  const handleDirectPrint = async () => {
    if (!selectedProduct) {
      toast.error('Selecciona un producto');
      return;
    }
    if (cantidad < 1) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    setLoading(true);
    try {
      await handlePrintEPLQZTray();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al imprimir');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!labelData) return;
    await handlePrintEPLQZTray();
  };

  // Enviar EPL usando QZ Tray (cliente)
  const handlePrintEPLQZTray = async () => {
    if (!window.qz || !qzReady) {
      toast.error('QZ Tray no está conectado. Por favor instala y ejecuta QZ Tray.');
      return;
    }

    try {
      // Generar comandos EPL
      const response = await axios.post(`${API}/labels/generate-epl`, {
        producto_id: selectedProduct.id,
        cantidad: cantidad,
        incluir_precio: incluirPrecio
      });

      const eplCommands = response.data.epl_commands;
      
      console.log('=== COMANDOS EPL GENERADOS ===');
      console.log(eplCommands);
      console.log('==============================');

      // Obtener lista de impresoras
      const printers = await qz.printers.find();
      console.log('Impresoras encontradas:', printers);

      if (!printers || printers.length === 0) {
        toast.error('No se encontraron impresoras');
        return;
      }

      // Si solo hay 1 impresora, imprimir directamente
      if (printers.length === 1) {
        await sendToQZPrinter(printers[0], eplCommands);
        return;
      }

      // Mostrar diálogo de selección de impresora
      setAvailablePrinters(printers);
      setSelectedPrinter(printers[0]);
      setPendingEplCommands(eplCommands);
      setShowPrinterSelect(true);

    } catch (error) {
      console.error('Error imprimiendo EPL (QZ Tray):', error);
      toast.error('Error al imprimir con QZ Tray: ' + (error.message || 'Error desconocido'));
    }
  };

  // Enviar comandos EPL a impresora seleccionada via QZ Tray
  const sendToQZPrinter = async (printerName, eplCommands) => {
    try {
      const config = qz.configs.create(printerName);
      const data = [{
        type: 'raw',
        format: 'plain',
        data: eplCommands
      }];

      console.log('Enviando a:', printerName);
      await qz.print(config, data);

      toast.success(`Impreso en: ${printerName}`);
      setShowPreview(false);
      setShowPrinterSelect(false);
      setPendingEplCommands(null);
    } catch (error) {
      console.error('Error enviando a impresora:', error);
      toast.error('Error al imprimir: ' + (error.message || 'Error desconocido'));
    }
  };

  // Confirmar impresión con impresora seleccionada
  const handleConfirmPrinter = async () => {
    if (!selectedPrinter || !pendingEplCommands) return;
    setLoading(true);
    await sendToQZPrinter(selectedPrinter, pendingEplCommands);
    setLoading(false);
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API}/label-config`, labelConfig);
      toast.success('Configuración guardada');
      setShowConfig(false);
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Impresión de Etiquetas</h1>
          <p className="text-slate-600">Genera e imprime etiquetas con código de barras para productos</p>
        </div>
        <Button onClick={() => setShowConfig(true)} variant="outline">
          <Settings className="mr-2" size={18} />
          Configuración
        </Button>
      </div>

      {/* Formulario principal */}
      <Card>
        <CardContent className="space-y-6 pt-6">
          {/* Selección de Productos - Estilo Facturación */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg text-slate-800 border-b pb-2">Selección de Productos</h3>

            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label htmlFor="product-code-input">Código Interno / Código de Barras</Label>
                <form onSubmit={handleProductCodeSubmit} className="flex gap-2">
                  <Input
                    id="product-code-input"
                    ref={productCodeRef}
                    value={productCodeInput}
                    onChange={(e) => setProductCodeInput(e.target.value)}
                    placeholder="Escriba o escanee el código..."
                    className="flex-1"
                    autoFocus
                    data-testid="product-code-input"
                  />
                  <Button type="submit" variant="outline" data-testid="add-product-btn">
                    Agregar
                  </Button>
                </form>
              </div>
              <Button
                type="button"
                onClick={() => setProductSearchOpen(true)}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="advanced-search-btn"
              >
                <Search size={16} className="mr-2" />
                Búsqueda Avanzada (F2)
              </Button>
            </div>

            <div className="text-xs text-slate-500 flex gap-4">
              <span><kbd className="px-2 py-1 bg-slate-100 rounded">F2</kbd> Búsqueda avanzada</span>
              <span><kbd className="px-2 py-1 bg-slate-100 rounded">F3</kbd> Focus en código</span>
            </div>
          </div>

          {/* Producto seleccionado */}
          {selectedProduct && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-semibold">Producto Seleccionado:</p>
                  <p className="text-lg font-bold text-blue-800">{selectedProduct.nombre}</p>
                  <p className="text-sm text-slate-700">
                    Código Interno: {selectedProduct.codigo_interno} | 
                    Código de Barras: {Array.isArray(selectedProduct.codigo_barras) ? (selectedProduct.codigo_barras.join(', ') || 'N/A') : (selectedProduct.codigo_barras || 'N/A')}
                  </p>
                  <p className="text-lg font-bold text-blue-700 mt-1">{formatCurrency(selectedProduct.precio_venta)}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedProduct(null);
                    setProductCodeInput('');
                    productCodeRef.current?.focus();
                  }}
                  data-testid="change-product-btn"
                >
                  Cambiar
                </Button>
              </div>
            </div>
          )}

          {!selectedProduct && (
            <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
              <Tag size={40} className="mx-auto text-slate-400 mb-3" />
              <p className="text-slate-600">No hay producto seleccionado</p>
              <p className="text-sm text-slate-500 mt-1">Use el campo de código o búsqueda avanzada (F2)</p>
            </div>
          )}

          {/* Opciones de impresión */}
          {selectedProduct && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cantidad">Cantidad de Etiquetas *</Label>
                <Input
                  id="cantidad"
                  type="number"
                  min="1"
                  value={cantidad}
                  onChange={(e) => setCantidad(parseInt(e.target.value) || 1)}
                  placeholder="Ej: 10"
                />
              </div>
              <div className="flex items-end">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="incluir-precio"
                    checked={incluirPrecio}
                    onCheckedChange={setIncluirPrecio}
                  />
                  <Label htmlFor="incluir-precio" className="cursor-pointer">
                    Incluir precio en etiqueta
                  </Label>
                </div>
              </div>
            </div>
          )}

          {/* Botones de acción */}
          {selectedProduct && (
            <div className="flex gap-3">
              <Button
                onClick={handleGenerateLabels}
                disabled={loading}
                variant="outline"
                className="flex-1"
                data-testid="preview-label-btn"
              >
                <Tag className="mr-2" size={18} />
                Vista Previa
              </Button>
              <Button
                onClick={handleDirectPrint}
                disabled={loading}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="direct-print-btn"
              >
                <Printer className="mr-2" size={18} />
                {loading ? 'Imprimiendo...' : `Imprimir (${cantidad})`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Vista Previa */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Vista Previa de Etiqueta</DialogTitle>
            <DialogDescription>
              Se imprimirán {labelData?.cantidad} etiqueta(s)
            </DialogDescription>
          </DialogHeader>

          {labelData && (
            <div className="space-y-4">
              {/* Preview de la etiqueta - Diseño tipo sticker cuadrado */}
              <div 
                className="border-2 border-slate-300 rounded-lg mx-auto overflow-hidden bg-white flex flex-col"
                style={{ 
                  width: '220px',
                  height: '220px',
                }}
                data-testid="label-preview"
              >
                <div className="flex flex-col flex-1 px-3 pt-3">
                  {/* Nombre del negocio */}
                  <div 
                    className="font-bold uppercase text-black leading-tight truncate"
                    style={{ fontSize: '11px', letterSpacing: '0.3px' }}
                    data-testid="label-store-name"
                  >
                    {labelData.sistema_nombre}
                  </div>

                  {/* Código de barras */}
                  <div className="my-1.5" style={{ height: '55px' }}>
                    {labelData.barcode_image ? (
                      <img 
                        src={labelData.barcode_image} 
                        alt="Barcode" 
                        className="h-full w-full"
                        style={{ 
                          objectFit: 'contain',
                          objectPosition: 'left center',
                          imageRendering: 'crisp-edges'
                        }}
                        data-testid="label-barcode"
                      />
                    ) : (
                      <div className="font-bold font-mono text-sm">
                        {labelData.codigo_barras}
                      </div>
                    )}
                  </div>

                  {/* Código + Nombre del producto */}
                  <div 
                    className="text-black leading-snug flex-1"
                    style={{ fontSize: '11px' }}
                    data-testid="label-product-info"
                  >
                    {labelData.codigo_interno} - {labelData.nombre_producto}
                  </div>
                </div>

                {/* Precio - Fondo negro, texto blanco */}
                {labelData.incluir_precio && labelData.precio && (
                  <div 
                    className="bg-black text-white font-bold px-3 py-2 mt-auto"
                    style={{ fontSize: '14px', letterSpacing: '0.3px' }}
                    data-testid="label-price"
                  >
                    RD:$ {labelData.precio.toFixed(2)}
                  </div>
                )}
              </div>

              {/* Información */}
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="font-semibold">Producto:</span> {labelData.nombre_producto}</p>
                <p><span className="font-semibold">Cantidad:</span> {labelData.cantidad} etiquetas</p>
                <p><span className="font-semibold">Etiqueta:</span> Precio 25 mm x 13 mm</p>
                <p><span className="font-semibold">Configuración:</span> {
                  labelConfig?.config_type === 'config_2connet_427b' ? '2Connet 427B' : labelConfig?.config_type
                }</p>
                <p><span className="font-semibold">Método:</span> EPL Directo (QZ Tray)</p>
              </div>

              {/* Botones */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowPreview(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={handlePrint} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="print-label-btn">
                  <Printer className="mr-2" size={18} />
                  Imprimir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Configuración */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configuración de Etiquetas</DialogTitle>
            <DialogDescription>Ajusta los parámetros de impresión</DialogDescription>
          </DialogHeader>

          {labelConfig && (
            <div className="space-y-4">
              <div>
                <Label>Tipo de Etiqueta</Label>
                <Select value={labelConfig.label_type || 'precio_2ftx1ft'} onValueChange={(value) => setLabelConfig({ ...labelConfig, label_type: value })}>
                  <SelectTrigger data-testid="label-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="precio_2ftx1ft">Etiqueta de Precio 2 ft x 1 ft</SelectItem>
                    <SelectItem value="precio_2ftx1ft_grande">Etiqueta de Precio 2 ft x 1 ft - Precio Grande</SelectItem>
                    <SelectItem value="precio_25x13">Etiqueta de Precio 25 mm x 13 mm</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Tipo de Configuración</Label>
                <Select value={labelConfig.config_type || 'config_2connet_427b'} onValueChange={(value) => setLabelConfig({ ...labelConfig, config_type: value })}>
                  <SelectTrigger data-testid="config-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="config_2connet_427b">Configuración 2Connet 427B</SelectItem>
                    <SelectItem value="config_2connet_281e">Configuración 2Connet 281E</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Método de Impresión</Label>
                <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700">
                  <Printer size={16} />
                  EPL Directo (QZ Tray)
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Envía comandos EPL directamente a la impresora vía QZ Tray
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="show-price-default"
                  checked={labelConfig.show_price_default}
                  onCheckedChange={(checked) => setLabelConfig({ ...labelConfig, show_price_default: checked })}
                  data-testid="show-price-checkbox"
                />
                <Label htmlFor="show-price-default" className="cursor-pointer">
                  Mostrar precio por defecto
                </Label>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowConfig(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={handleSaveConfig} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-config-btn">
                  Guardar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Búsqueda Avanzada de Productos */}
      <Dialog open={productSearchOpen} onOpenChange={setProductSearchOpen}>
        <DialogContent className="max-w-6xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Búsqueda Avanzada de Productos</DialogTitle>
            <DialogDescription>Busque productos por nombre, código o categoría</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por nombre, código interno, código de barras o categoría..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="pl-10"
                autoFocus
                data-testid="advanced-search-input"
              />
            </div>

            <div className="border rounded-lg max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Cód. Interno</th>
                    <th className="text-left p-2">Producto</th>
                    <th className="text-left p-2">Categoría</th>
                    <th className="text-right p-2">Precio</th>
                    <th className="text-center p-2">Stock</th>
                    <th className="text-center p-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProductsForSearch.map(product => (
                    <tr key={product.id} className="border-b hover:bg-slate-50">
                      <td className="p-2 text-slate-700 font-mono text-xs">{product.codigo_interno || '-'}</td>
                      <td className="p-2">
                        <div className="font-medium text-slate-800">{product.nombre}</div>
                        <div className="text-xs text-slate-500">{product.unidad_medida}</div>
                      </td>
                      <td className="p-2 text-slate-600">{product.categoria || '-'}</td>
                      <td className="p-2 text-right font-semibold text-green-600">{formatCurrency(product.precio_venta)}</td>
                      <td className="p-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          product.existencia < 10 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {product.existencia}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedProduct(product);
                            setProductSearchOpen(false);
                            setProductSearchTerm('');
                            toast.success(`${product.nombre} seleccionado`);
                          }}
                          className="bg-blue-500 hover:bg-blue-600"
                          data-testid={`select-product-${product.codigo_interno}`}
                        >
                          Seleccionar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredProductsForSearch.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No se encontraron productos
                </div>
              )}
            </div>

            <div className="flex justify-between items-center text-sm text-slate-600 bg-slate-50 p-3 rounded">
              <span>Haga clic en un producto para seleccionarlo</span>
              <span className="font-semibold">{filteredProductsForSearch.length} productos encontrados</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Seleccionar Impresora */}
      <Dialog open={showPrinterSelect} onOpenChange={(open) => { if (!open) { setShowPrinterSelect(false); setPendingEplCommands(null); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Seleccionar Impresora</DialogTitle>
            <DialogDescription>
              Seleccione la impresora donde desea imprimir las etiquetas
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              {availablePrinters.map((printer, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    selectedPrinter === printer
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={() => setSelectedPrinter(printer)}
                  data-testid={`printer-option-${idx}`}
                >
                  <Printer size={18} className={selectedPrinter === printer ? 'text-blue-600' : 'text-slate-400'} />
                  <span className={`text-sm font-medium ${selectedPrinter === printer ? 'text-blue-700' : 'text-slate-700'}`}>
                    {printer}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowPrinterSelect(false); setPendingEplCommands(null); }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmPrinter}
                disabled={loading || !selectedPrinter}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="confirm-printer-btn"
              >
                <Printer className="mr-2" size={18} />
                {loading ? 'Imprimiendo...' : 'Imprimir'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabelPrinting;
